import { type ReactElement } from 'react';
import { Card, CardContent } from '@citibox-panel/ui';
import type { ExclusionListCarrier, ExclusionListSeams } from '../lib/excludedUser';
import { CarrierExclusionSection } from './CarrierExclusionSection';
import { PageIntro } from './PageIntro';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface CarrierExclusionListsPageProps extends ExclusionListSeams {
  // The catalog of carriers that have a list, in display order. The host owns it.
  carriers: readonly ExclusionListCarrier[];
  // The panel's own IANA zone for the «Desde» column; `null` renders in UTC.
  timeZone: string | null;
  // Set by the host only for a move made from inside the panel.
  focusHeadingOnArrival?: boolean;
  // Errors the host already answers with a screen of its own, so no card paints them a second time.
  // Left out, every error stays on its card.
  isHandledByHost?: IsHandledByHost;
}

// The band and the navigation around this screen are the host's layout: this module resolves no
// config and knows no route. It opens no `main` landmark — the host's authenticated layout does.
export function CarrierExclusionListsPage({
  carriers,
  timeZone,
  fetchExcludedUsers,
  addExcludedUser,
  removeExcludedUser,
  focusHeadingOnArrival = false,
  isHandledByHost = neverHandledByHost,
}: CarrierExclusionListsPageProps): ReactElement {
  return (
    // One white, rounded panel for everything the page says, the same surface, inset and padding
    // as door-access and every other card at this level.
    <Card variant="outlined" sx={{ borderRadius: 3, m: 2 }}>
      <CardContent>
        <PageIntro focusHeadingOnArrival={focusHeadingOnArrival} />
        {carriers.map((carrier) => (
          <CarrierExclusionSection
            key={carrier.slug}
            carrier={carrier}
            timeZone={timeZone}
            fetchExcludedUsers={fetchExcludedUsers}
            addExcludedUser={addExcludedUser}
            removeExcludedUser={removeExcludedUser}
            isHandledByHost={isHandledByHost}
          />
        ))}
      </CardContent>
    </Card>
  );
}
